import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginMethod } from '../types';
import { Logo } from '../components/Logo';
import { Input } from '../components/Input';
import { OTPInput } from '../components/OTPInput';
import { PhoneInput } from '../components/PhoneInput';
import { Loader2, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { loadTurnstileScript, refreshAuthProfile, resolveApiBaseUrl, syncAuthToExtension } from '../lib/utils';

type AuthVariant = 'page' | 'modal'

interface LoginScreenProps {
  variant?: AuthVariant
  onClose?: () => void
  onSwitch?: () => void
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ variant = 'page', onClose, onSwitch }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [method, setMethod] = useState<LoginMethod>('sms');
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // SMS Code State
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [debugCode, setDebugCode] = useState<string | null>(null);

  const [turnstileState, setTurnstileState] = useState<{ status: 'loading' | 'enabled' | 'disabled'; siteKey: string }>(
    { status: 'loading', siteKey: '' }
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  const persistAuth = (payload: { token?: string } | null) => {
    if (!payload?.token) {
      return false;
    }
    try {
      localStorage.setItem('token', payload.token);
      localStorage.setItem('auth', JSON.stringify(payload ?? {}));
      localStorage.removeItem('guest_token');
      localStorage.removeItem('guest_user');
    } catch (error) {
      // ignore storage errors
    }
    return true;
  };

  // Countdown timer logic
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    setSendError(null);
    setOtpError(null);
    setPasswordError(null);
    setDebugCode(null);
  }, [method]);

  useEffect(() => {
    let active = true;
    const fetchTurnstileConfig = async () => {
      try {
        const apiBaseUrl = resolveApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/v1/auth/turnstile`);
        const payload = await response.json().catch(() => null);
        if (!active) return;
        if (response.ok && payload?.enabled && payload?.site_key) {
          setTurnstileState({ status: 'enabled', siteKey: payload.site_key });
          setTurnstileError(null);
        } else {
          setTurnstileState({ status: 'disabled', siteKey: '' });
          setTurnstileError(t('captchaUnavailable'));
        }
      } catch (error) {
        if (active) {
          setTurnstileState({ status: 'disabled', siteKey: '' });
          setTurnstileError(t('captchaUnavailable'));
        }
      }
    };
    fetchTurnstileConfig();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (turnstileState.status !== 'enabled' || !turnstileState.siteKey || !turnstileRef.current) {
      return;
    }
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !turnstileRef.current || !window.turnstile) {
          return;
        }
        if (turnstileWidgetId.current && window.turnstile.remove) {
          window.turnstile.remove(turnstileWidgetId.current);
          turnstileWidgetId.current = null;
        }
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileState.siteKey,
          callback: (token: string) => {
            setTurnstileToken(token);
            setTurnstileError(null);
          },
          'expired-callback': () => {
            setTurnstileToken(null);
          },
          'error-callback': () => {
            setTurnstileToken(null);
            setTurnstileError(t('captchaError'));
          }
        });
      })
      .catch(() => {
        if (!cancelled) {
          setTurnstileError(t('captchaLoadFailed'));
        }
      });
    return () => {
      cancelled = true;
      if (turnstileWidgetId.current && window.turnstile?.remove) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [turnstileState.status, turnstileState.siteKey, t]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedPhone = phone.replace(/[^\d]+/g, '');
    if (!trimmedPhone) {
      setSendError(t('phoneRequired'));
      return;
    }
    if (trimmedPhone.length !== 11) {
      setSendError(t('phoneInvalid'));
      return;
    }
    if (method === 'sms') {
      if (!otpValue || otpValue.length !== 6) {
        setOtpError(t('invalidCode'));
        return;
      }

      setLoading(true);
      setOtpError(null);

      try {
        const apiBaseUrl = resolveApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/v1/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: trimmedPhone,
            code: otpValue,
            device: {
              userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
              platform: typeof navigator !== 'undefined' ? navigator.platform : ''
            }
          })
        });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        setOtpError(payload?.detail || t('invalidCode'));
        setLoading(false);
        return;
      }
      if (!persistAuth(payload)) {
        setOtpError(t('loginFailed'));
        setLoading(false);
        return;
      }
      const refreshed = await refreshAuthProfile();
      await syncAuthToExtension(payload?.token ?? refreshed?.token ?? '', refreshed?.user ?? payload?.user ?? null, apiBaseUrl);

      setOtpSuccess(true);
      setLoading(false);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth:updated'));
      }
      setTimeout(() => {
        if (onClose) {
          onClose();
          return;
        }
          navigate('/workspace');
        }, 900);
      } catch (error) {
        setOtpError(t('loginFailed'));
        setLoading(false);
      }
      return;
    }

    if (!password) {
      setPasswordError(t('passwordRequired'));
      return;
    }
    if (password.length < 8) {
      setPasswordError(t('passwordTooShort'));
      return;
    }

    setLoading(true);
    setPasswordError(null);

    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/login/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: trimmedPhone,
          password,
          device: {
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            platform: typeof navigator !== 'undefined' ? navigator.platform : ''
          }
        })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        setPasswordError(payload?.detail || t('loginFailed'));
        setLoading(false);
        return;
      }
      if (!persistAuth(payload)) {
        setPasswordError(t('loginFailed'));
        setLoading(false);
        return;
      }
      const refreshed = await refreshAuthProfile();
      await syncAuthToExtension(payload?.token ?? refreshed?.token ?? '', refreshed?.user ?? payload?.user ?? null, apiBaseUrl);

      setLoading(false);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth:updated'));
      }
      setTimeout(() => {
        if (onClose) {
          onClose();
          return;
        }
        navigate('/workspace');
      }, 900);
    } catch (error) {
      setPasswordError(t('loginFailed'));
      setLoading(false);
    }
  };

  const resetTurnstile = () => {
    if (turnstileWidgetId.current && window.turnstile?.reset) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
    setTurnstileToken(null);
  };

  const handleSendCode = async () => {
    if (loading) return;
    const trimmedPhone = phone.replace(/[^\d]+/g, '');
    if (!trimmedPhone) {
      setSendError(t('phoneRequired'));
      return;
    }
    if (trimmedPhone.length !== 11) {
      setSendError(t('phoneInvalid'));
      return;
    }
    if (turnstileState.status === 'loading') {
      setTurnstileError(t('captchaLoading'));
      return;
    }
    if (turnstileState.status !== 'enabled') {
      setTurnstileError(t('captchaUnavailable'));
      return;
    }
    if (!turnstileToken) {
      setTurnstileError(t('captchaRequired'));
      return;
    }

    setLoading(true);
    setSendError(null);
    setDebugCode(null);
    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: trimmedPhone,
          turnstile_token: turnstileState.status === 'enabled' ? turnstileToken : undefined
        })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const retryAfter = Number(response.headers.get('Retry-After') || 0);
        if (response.status === 429 && retryAfter > 0) {
          setCountdown(retryAfter);
        }
        setSendError(payload?.detail || payload?.message || t('sendCodeFailed'));
        setLoading(false);
        resetTurnstile();
        return;
      }

      setCodeSent(true);
      setCountdown(60);
      setOtpValue('');
      setOtpError(null);
      setOtpSuccess(false);
      setTurnstileError(null);
      if (payload?.debug_code) {
        setDebugCode(String(payload.debug_code));
      }
      setLoading(false);
      resetTurnstile();
    } catch (error) {
      setSendError(t('sendCodeFailed'));
      setLoading(false);
      resetTurnstile();
    }
  };

  const handleOtpChange = (code: string) => {
      setOtpValue(code);
      if (otpError) setOtpError(null);
      if (otpSuccess) setOtpSuccess(false);
  };

  const isModal = variant === 'modal'
  const handleSwitch = () => {
    if (onSwitch) {
      onSwitch()
      return
    }
    navigate('/signup')
  }

  return (
    <div
      data-auth-variant={variant}
      className={isModal ? 'w-full' : 'min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4'}
    >
    <div
      className={`relative w-full bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-200/60 p-10 md:p-12 transition-all ${
        isModal ? '' : 'max-w-[440px]'
      }`}
    >
      {isModal && onClose && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      )}
      <div className="flex flex-col items-center text-center mb-8">
        <div className="mb-6">
          <Logo />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2 tracking-tight">{t('welcome')}</h1>
        <p className="text-slate-500 text-sm leading-relaxed max-w-[280px]">
          {t('loginSubtitle')}
        </p>
      </div>

      <div className="p-1.5 bg-slate-100 rounded-xl flex shadow-inner mb-8">
        <button
          onClick={() => setMethod('sms')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all active:scale-95 ${
            method === 'sms'
              ? 'bg-white text-slate-900 shadow-sm ring-1 ring-black/5'
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
          }`}
        >
          {t('smsMethod')}
        </button>
        <button
          onClick={() => setMethod('password')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all active:scale-95 ${
            method === 'password'
              ? 'bg-white text-slate-900 shadow-sm ring-1 ring-black/5'
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
          }`}
        >
          {t('passMethod')}
        </button>
      </div>

      <form onSubmit={handleLogin} className="space-y-6">
        <div className="space-y-3">
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
            {t('phoneLabel')}
          </label>
          <PhoneInput
            disabled={method === 'sms' && (codeSent || otpSuccess)}
            placeholder={t('phonePlaceholder')}
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
              if (sendError) setSendError(null);
            }}
          />

          {method === 'password' && sendError && (
            <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
              <AlertCircle size={14} />
              <span className="text-xs font-medium">{sendError}</span>
            </div>
          )}

          {method === 'sms' && (
            <div className="space-y-2" data-turnstile-container="true">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
                {t('captchaLabel')}
              </label>
              <div className="relative rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-center min-h-[72px]">
                <div ref={turnstileRef} className="w-full flex justify-center" />
                {turnstileState.status === 'loading' && (
                  <span className="absolute text-xs text-slate-400">{t('captchaLoading')}</span>
                )}
                {turnstileState.status === 'disabled' && (
                  <span className="absolute text-xs text-slate-400">{t('captchaUnavailable')}</span>
                )}
              </div>
              <p className="text-xs text-slate-500">{t('captchaHint')}</p>
              {turnstileError && (
                <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                  <AlertCircle size={14} />
                  <span className="text-xs font-medium">{turnstileError}</span>
                </div>
              )}
            </div>
          )}

          {method === 'sms' && !codeSent && (
             <div className="pt-2">
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={loading}
                  className="w-full flex items-center justify-center py-3.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-xl font-semibold transition-all text-sm active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : t('sendCode')}
                </button>
                {sendError && (
                  <div className="mt-2 flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                    <AlertCircle size={14} />
                    <span className="text-xs font-medium">{sendError}</span>
                  </div>
                )}
             </div>
          )}
        </div>

        {method === 'sms' && codeSent && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
             <div className="flex justify-between items-center">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
                  {t('verifyLabel')}
                </label>
                <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={countdown > 0 || loading || otpSuccess}
                    className={`text-[11px] font-bold uppercase tracking-wider transition-colors ${
                        countdown > 0 || otpSuccess
                        ? 'text-slate-400 cursor-default'
                        : 'text-primary hover:text-blue-700 cursor-pointer'
                    }`}
                >
                    {countdown > 0
                        ? t('resendIn').replace('{time}', countdown.toString())
                        : t('resendNow')}
                </button>
             </div>

            <div className="space-y-2">
                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <OTPInput
                            length={6}
                            onChange={handleOtpChange}
                            error={!!otpError}
                            success={otpSuccess}
                            readOnly={otpSuccess}
                        />
                    </div>
                    {otpSuccess && (
                        <div className="flex-shrink-0 text-success animate-in zoom-in duration-300">
                            <CheckCircle2 size={28} strokeWidth={2.5} className="fill-success/10" />
                        </div>
                    )}
                </div>

                {otpError && (
                    <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                        <AlertCircle size={14} />
                        <span className="text-xs font-medium">{otpError}</span>
                    </div>
                )}
                {sendError && !otpError && (
                    <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                        <AlertCircle size={14} />
                        <span className="text-xs font-medium">{sendError}</span>
                    </div>
                )}
                {debugCode && (
                    <div className="text-xs text-slate-500">
                      {t('debugCodeHint').replace('{code}', debugCode)}
                    </div>
                )}
            </div>
          </div>
        )}

        {method === 'password' && (
          <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex justify-between items-center">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">
                {t('passwordLabel')}
              </label>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-xs font-semibold text-primary hover:text-blue-700 transition-colors"
              >
                {t('forgotPass')}
              </button>
            </div>
            <Input
              type="password"
              placeholder={t('passPlaceholder')}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (passwordError) setPasswordError(null);
              }}
              error={passwordError || ''}
            />
            {sendError && !passwordError && (
              <div className="flex items-center gap-1.5 text-error animate-in slide-in-from-top-1 duration-200">
                <AlertCircle size={14} />
                <span className="text-xs font-medium">{sendError}</span>
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || otpSuccess}
          className="w-full bg-primary hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 active:scale-[0.98] mt-2 flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="animate-spin" size={20} /> : t('signIn')}
        </button>
      </form>

      <div className="text-center mt-8">
        <p className="text-sm text-slate-500">
          {t('newToApp')}{' '}
          <button
            onClick={handleSwitch}
            className="text-primary font-bold hover:underline ml-1 hover:text-blue-700 transition-colors"
          >
            {t('signUp')}
          </button>
        </p>
      </div>
    </div>
    </div>
  );
};
